<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { CheckCircle2, Clock3, Cpu, ListTree, Pencil, Plus, Power, RefreshCw, Save, Send, SlidersHorizontal, TimerReset, Trash2, Wifi, WifiOff } from "lucide-vue-next";
import { api } from "../services/api";
import type { Device, DeviceAction, MqttServiceConfig } from "../types";

type ActionDraft = Partial<DeviceAction> & { payloadText: string };
const devices = ref<Device[]>([]);
const actions = ref<DeviceAction[]>([]);
const selectedId = ref("");
const selectedActionId = ref("");
const activeTab = ref<"devices" | "actions">("devices");
const actionDraft = ref<ActionDraft>(newActionDraft());
const loading = ref(false);
const sending = ref(false);
const savingAction = ref(false);
const savingDevice = ref(false);
const savingSettings = ref(false);
const deviceDialogOpen = ref(false);
const settingsDialogOpen = ref(false);
const deviceDialogMode = ref<"create" | "edit">("create");
const editingDeviceId = ref("");
const deviceDraft = ref({ sceneKey: "home", deviceCode: "", name: "" });
const mqttConfig = ref<MqttServiceConfig>();
const commandTimeoutSeconds = ref(30);
const lastRefreshedAt = ref<Date>();
const refreshingDevices = ref(false);
const refreshError = ref(false);
const settingsError = ref("");
const error = ref("");
const success = ref("");
const fieldKey = ref("switch");
const valueType = ref<"boolean" | "number" | "text">("boolean");
const commandValue = ref("true");
let timer: number | undefined;

const selectedDevice = computed(() => devices.value.find((device) => device.id === selectedId.value) ?? devices.value[0]);
const selectedDeviceActions = computed(() => actions.value.filter((action) => action.deviceId === selectedId.value));
const refreshStatusText = computed(() => refreshError.value
  ? "刷新暂不可用"
  : lastRefreshedAt.value
    ? `${lastRefreshedAt.value.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} 已更新`
    : "等待首次同步");

function applyDevices(nextDevices: Device[]): void {
  devices.value = nextDevices;
  if (!selectedId.value || !nextDevices.some((device) => device.id === selectedId.value)) {
    selectedId.value = nextDevices[0]?.id ?? "";
  }
  lastRefreshedAt.value = new Date();
  refreshError.value = false;
}

function setActiveTab(tab: "devices" | "actions"): void {
  activeTab.value = tab;
}

function newActionDraft(): ActionDraft {
  return {
    deviceId: selectedId.value,
    actionKey: "",
    name: "",
    description: "",
    topic: "",
    payloadText: JSON.stringify({ commandType: "custom", payload: { action: "" } }, null, 2),
    waitForAck: true,
    successReply: "动作已执行。",
    failureReply: "动作执行失败：{{reason}}",
    timeoutReply: "设备暂未回应。",
    isEnabled: true,
    agentIds: [],
  };
}

async function load(quiet = false): Promise<void> {
  if (!quiet) loading.value = true;
  try {
    const [nextDevices, nextActions, nextMqttConfig] = await Promise.all([
      api.adminDevices(), api.adminDeviceActions(), api.mqttServiceConfig(),
    ]);
    applyDevices(nextDevices);
    actions.value = nextActions;
    mqttConfig.value = nextMqttConfig;
    commandTimeoutSeconds.value = nextMqttConfig.commandTimeoutMs / 1000;
    if (selectedActionId.value) {
      const current = actions.value.find((action) => action.id === selectedActionId.value);
      if (current) selectAction(current);
    }
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "设备配置加载失败";
  } finally {
    loading.value = false;
  }
}

async function refreshDevices(quiet = true): Promise<void> {
  if (refreshingDevices.value || document.visibilityState === "hidden") return;
  refreshingDevices.value = true;
  if (!quiet) loading.value = true;
  try {
    applyDevices(await api.adminDevices());
  } catch (reason) {
    refreshError.value = true;
    if (!quiet) error.value = reason instanceof Error ? reason.message : "设备状态刷新失败";
  } finally {
    refreshingDevices.value = false;
    if (!quiet) loading.value = false;
  }
}

function openSettingsDialog(): void {
  commandTimeoutSeconds.value = (mqttConfig.value?.commandTimeoutMs ?? 30_000) / 1000;
  settingsError.value = "";
  settingsDialogOpen.value = true;
}

async function saveSettings(): Promise<void> {
  const seconds = Number(commandTimeoutSeconds.value);
  if (!Number.isInteger(seconds) || seconds < 5 || seconds > 300) {
    settingsError.value = "等待超时必须是 5 到 300 之间的整数秒";
    return;
  }
  savingSettings.value = true;
  settingsError.value = "";
  try {
    mqttConfig.value = await api.saveMqttServiceConfig(seconds * 1000);
    settingsDialogOpen.value = false;
    success.value = `设备操作等待超时已设为 ${seconds} 秒，新命令立即生效`;
  } catch (reason) {
    settingsError.value = reason instanceof Error ? reason.message : "设备控制设置保存失败";
  } finally {
    savingSettings.value = false;
  }
}

function openDeviceDialog(): void {
  deviceDialogMode.value = "create";
  editingDeviceId.value = "";
  deviceDraft.value = { sceneKey: "home", deviceCode: "", name: "" };
  error.value = "";
  success.value = "";
  deviceDialogOpen.value = true;
}

function openEditDevice(): void {
  if (!selectedDevice.value) return;
  deviceDialogMode.value = "edit";
  editingDeviceId.value = selectedDevice.value.id;
  deviceDraft.value = {
    sceneKey: selectedDevice.value.sceneKey,
    deviceCode: selectedDevice.value.deviceCode,
    name: selectedDevice.value.name ?? "",
  };
  error.value = "";
  success.value = "";
  deviceDialogOpen.value = true;
}

async function saveDevice(): Promise<void> {
  const sceneKey = deviceDraft.value.sceneKey.trim();
  const deviceCode = deviceDraft.value.deviceCode.trim();
  if (!sceneKey || !deviceCode) return;
  savingDevice.value = true;
  error.value = "";
  success.value = "";
  try {
    const input = { sceneKey, deviceCode, name: deviceDraft.value.name.trim() || null };
    const device = deviceDialogMode.value === "create"
      ? await api.createDevice(input)
      : await api.updateDevice(editingDeviceId.value, input);
    selectedId.value = device.id;
    deviceDialogOpen.value = false;
    await load(true);
    success.value = deviceDialogMode.value === "create"
      ? "设备已新增，等待设备通过 MQTT 上报在线状态"
      : "设备信息已保存";
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : deviceDialogMode.value === "create" ? "设备新增失败" : "设备保存失败";
  } finally {
    savingDevice.value = false;
  }
}

function selectAction(action: DeviceAction): void {
  selectedActionId.value = action.id;
  selectedId.value = action.deviceId;
  actionDraft.value = { ...action, payloadText: JSON.stringify(action.payload, null, 2) };
  success.value = "";
  error.value = "";
}

function startActionCreate(): void {
  selectedActionId.value = "";
  actionDraft.value = newActionDraft();
  success.value = "";
  error.value = "";
}

async function saveAction(): Promise<void> {
  if (!selectedDevice.value || !actionDraft.value.name?.trim() || !actionDraft.value.actionKey?.trim() || !actionDraft.value.description?.trim()) return;
  let payload: Record<string, unknown>;
  try {
    const parsed = JSON.parse(actionDraft.value.payloadText) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error();
    payload = parsed as Record<string, unknown>;
  } catch {
    error.value = "MQTT 载荷必须是有效的 JSON 对象";
    return;
  }
  savingAction.value = true;
  error.value = "";
  success.value = "";
  try {
    const saved = await api.saveDeviceAction({
      id: selectedActionId.value || undefined,
      deviceId: selectedId.value,
      actionKey: actionDraft.value.actionKey.trim(),
      name: actionDraft.value.name.trim(),
      description: actionDraft.value.description.trim(),
      topic: actionDraft.value.topic?.trim() || null,
      payload,
      waitForAck: actionDraft.value.waitForAck ?? true,
      successReply: actionDraft.value.successReply?.trim() || "动作已执行。",
      failureReply: actionDraft.value.failureReply?.trim() || "动作执行失败：{{reason}}",
      timeoutReply: actionDraft.value.timeoutReply?.trim() || "设备暂未回应。",
      isEnabled: actionDraft.value.isEnabled ?? true,
    });
    selectedActionId.value = saved.id;
    await load(true);
    success.value = "自定义动作已保存";
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "动作保存失败";
  } finally {
    savingAction.value = false;
  }
}

async function removeAction(): Promise<void> {
  if (!selectedActionId.value || !window.confirm("确定删除这个自定义动作？")) return;
  try {
    await api.deleteDeviceAction(selectedActionId.value);
    startActionCreate();
    await load(true);
    success.value = "自定义动作已删除";
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "动作删除失败";
  }
}

async function sendCommand(): Promise<void> {
  if (!selectedDevice.value || !fieldKey.value.trim()) return;
  sending.value = true;
  error.value = "";
  success.value = "";
  const value = valueType.value === "boolean" ? commandValue.value === "true" : valueType.value === "number" ? Number(commandValue.value) : commandValue.value;
  try {
    await api.sendCommand(selectedDevice.value.id, fieldKey.value.trim(), value);
    success.value = `命令已进入等待 ACK 状态，最长等待 ${commandTimeoutSeconds.value} 秒`;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "命令发送失败";
  } finally {
    sending.value = false;
  }
}

function formatTime(value?: string | null): string {
  return value ? new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(value)) : "—";
}

watch(selectedId, () => {
  if (!selectedActionId.value) actionDraft.value.deviceId = selectedId.value;
});
onMounted(() => {
  void load();
  timer = window.setInterval(() => void refreshDevices(), 5000);
  document.addEventListener("visibilitychange", handleVisibilityChange);
});
function handleVisibilityChange(): void {
  if (document.visibilityState === "visible") void refreshDevices();
}
onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});
</script>

<template>
  <div class="device-admin-view">
    <div class="view-tabs" role="tablist" aria-label="设备管理视图">
      <button type="button" :class="{ active: activeTab === 'devices' }" @click="setActiveTab('devices')"><SlidersHorizontal :size="16" />设备与属性</button>
      <button type="button" :class="{ active: activeTab === 'actions' }" @click="setActiveTab('actions')"><ListTree :size="16" />自定义动作</button>
    </div>

    <div v-if="activeTab === 'devices'" class="device-layout">
      <section class="surface-section device-table-section">
        <div class="section-header">
          <div><span class="eyebrow">MQTT</span><h2>设备列表</h2></div>
          <div class="header-command-group">
            <div :class="['auto-refresh-state', { error: refreshError }]" title="设备在线状态每 5 秒自动同步">
              <RefreshCw :size="15" :class="{ spinning: refreshingDevices }" />
              <span><strong>自动刷新</strong><small>{{ refreshStatusText }}</small></span>
            </div>
            <button class="icon-button compact" type="button" title="设备控制设置" @click="openSettingsDialog"><TimerReset :size="16" /></button>
            <button class="button primary compact-button" type="button" @click="openDeviceDialog"><Plus :size="16" />新增设备</button>
            <button class="icon-button compact" type="button" title="立即刷新" :disabled="loading || refreshingDevices" @click="refreshDevices(false)"><RefreshCw :size="16" :class="{ spinning: loading || refreshingDevices }" /></button>
          </div>
        </div>
        <div class="table-scroll">
          <table class="data-table">
            <thead><tr><th>状态</th><th>设备</th><th>场景</th><th>最近上报</th></tr></thead>
            <tbody>
              <tr v-for="device in devices" :key="device.id" :class="{ selected: selectedDevice?.id === device.id }" @click="selectedId = device.id">
                <td><span :class="['device-status', device.status.toLowerCase()]"><Wifi v-if="device.status === 'ONLINE'" :size="14" /><WifiOff v-else :size="14" />{{ device.status }}</span></td>
                <td><strong>{{ device.name || device.deviceCode }}</strong><small>{{ device.deviceCode }}</small></td>
                <td>{{ device.sceneKey }}</td>
                <td>{{ formatTime(device.lastSeenAt) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="!devices.length" class="empty-state">暂无设备</div>
        </div>
      </section>

      <aside class="surface-section device-detail">
        <div class="section-header">
          <div><span class="eyebrow">属性测试</span><h2>{{ selectedDevice?.deviceCode ?? "未选择设备" }}</h2></div>
          <button class="icon-button compact" type="button" title="编辑设备" :disabled="!selectedDevice" @click="openEditDevice"><Pencil :size="16" /></button>
        </div>
        <div v-if="selectedDevice" class="state-grid">
          <div v-for="(value, key) in selectedDevice.reportedState" :key="key"><span>{{ key }}</span><strong>{{ typeof value === "object" ? JSON.stringify(value) : value }}</strong></div>
        </div>
        <form class="command-form" @submit.prevent="sendCommand">
          <p class="command-timeout-note"><Clock3 :size="14" />等待设备 ACK：{{ commandTimeoutSeconds }} 秒</p>
          <label class="form-field"><span>字段</span><input v-model="fieldKey" /></label>
          <div class="inline-fields">
            <label class="form-field"><span>类型</span><select v-model="valueType"><option value="boolean">布尔</option><option value="number">数值</option><option value="text">文本</option></select></label>
            <label class="form-field"><span>值</span><select v-if="valueType === 'boolean'" v-model="commandValue"><option value="true">true</option><option value="false">false</option></select><input v-else v-model="commandValue" :type="valueType === 'number' ? 'number' : 'text'" /></label>
          </div>
          <button class="button primary full" type="submit" :disabled="!selectedDevice || sending"><Power :size="17" />{{ sending ? "发送中" : "发送命令" }}</button>
        </form>
      </aside>
    </div>

    <div v-else class="action-layout">
      <aside class="surface-section action-list-panel">
        <div class="section-header">
          <div><span class="eyebrow">Function Calling</span><h2>动作列表</h2></div>
          <button class="icon-button compact" type="button" title="新建动作" :disabled="!selectedDevice" @click="startActionCreate"><Plus :size="17" /></button>
        </div>
        <label class="form-field action-device-filter"><span>设备</span><select v-model="selectedId"><option v-for="device in devices" :key="device.id" :value="device.id">{{ device.name || device.deviceCode }}</option></select></label>
        <button v-for="action in selectedDeviceActions" :key="action.id" type="button" :class="['action-list-item', { active: selectedActionId === action.id }]" @click="selectAction(action)">
          <span :class="['status-dot', action.isEnabled ? 'ok' : 'bad']" />
          <div><strong>{{ action.name }}</strong><small>{{ action.actionKey }}</small></div>
        </button>
        <div v-if="!selectedDeviceActions.length" class="empty-state">该设备暂无自定义动作</div>
      </aside>

      <section class="surface-section action-editor">
        <div class="section-header">
          <div><span class="eyebrow">{{ selectedActionId ? "编辑动作" : "新建动作" }}</span><h2>{{ actionDraft.name || "动作配置" }}</h2></div>
          <div class="header-command-group">
            <button v-if="selectedActionId" class="icon-button danger-icon" type="button" title="删除动作" @click="removeAction"><Trash2 :size="17" /></button>
            <button class="button primary" type="button" :disabled="savingAction || !selectedDevice" @click="saveAction"><Save :size="16" />{{ savingAction ? "保存中" : "保存" }}</button>
          </div>
        </div>
        <div class="action-form-grid">
          <label class="form-field"><span>动作名称</span><input v-model="actionDraft.name" placeholder="启动飞行模式" /></label>
          <label class="form-field"><span>动作标识</span><input v-model="actionDraft.actionKey" placeholder="start_flight_mode" :disabled="Boolean(selectedActionId)" /></label>
          <label class="form-field span-2"><span>触发说明</span><textarea v-model="actionDraft.description" rows="2" placeholder="用户要求启动、进入或开启飞行模式时执行" /></label>
          <label class="form-field span-2"><span>MQTT Topic</span><input v-model="actionDraft.topic" :placeholder="selectedDevice ? `iot/${selectedDevice.sceneKey}/${selectedDevice.deviceCode}/command（留空使用默认）` : ''" /></label>
          <label class="form-field span-2 payload-field"><span>MQTT JSON 载荷</span><textarea v-model="actionDraft.payloadText" spellcheck="false" /><small>后台发布时会在 JSON 根节点注入最新命令 id。</small></label>
          <label class="form-field span-2"><span>成功回复</span><input v-model="actionDraft.successReply" /></label>
          <label class="form-field"><span>失败回复</span><input v-model="actionDraft.failureReply" /></label>
          <label class="form-field"><span>超时回复</span><input v-model="actionDraft.timeoutReply" /></label>
        </div>
        <div class="action-options">
          <label class="toggle-control"><input v-model="actionDraft.waitForAck" type="checkbox" /><span class="toggle-track" /><span>等待设备 ACK</span></label>
          <label class="toggle-control"><input v-model="actionDraft.isEnabled" type="checkbox" /><span class="toggle-track" /><span>启用动作</span></label>
        </div>
      </section>
    </div>
    <p v-if="success" class="form-success"><CheckCircle2 :size="16" />{{ success }}</p>
    <p v-if="error && !deviceDialogOpen" class="form-error"><Send :size="16" />{{ error }}</p>

    <div v-if="deviceDialogOpen" class="modal-backdrop" @click.self="deviceDialogOpen = false">
      <form class="modal-panel" @submit.prevent="saveDevice">
        <div class="section-header">
          <div><span class="eyebrow">MQTT 设备</span><h2>{{ deviceDialogMode === "create" ? "新增设备" : "编辑设备" }}</h2></div>
          <Cpu :size="20" />
        </div>
        <div class="form-stack">
          <label class="form-field"><span>场景标识</span><input v-model="deviceDraft.sceneKey" placeholder="home" autocomplete="off" /><small>对应 Topic 中的 sceneKey，例如 iot/home/...</small></label>
          <label class="form-field"><span>设备编码</span><input v-model="deviceDraft.deviceCode" placeholder="light-02" autocomplete="off" autofocus /><small>同一场景内必须唯一，例如 light-02</small></label>
          <label class="form-field"><span>设备名称（可选）</span><input v-model="deviceDraft.name" placeholder="卧室灯" autocomplete="off" /></label>
        </div>
        <p class="form-hint">
          {{ deviceDialogMode === "create"
            ? "新增后设备状态为 UNKNOWN；设备向 status 或 telemetry Topic 上报后会自动更新状态。"
            : "修改场景标识或设备编码会改变 MQTT Topic，保存后设备状态会重置为 UNKNOWN。" }}
        </p>
        <p v-if="error" class="form-error"><Send :size="16" />{{ error }}</p>
        <div class="modal-actions">
          <button class="button secondary" type="button" :disabled="savingDevice" @click="deviceDialogOpen = false">取消</button>
          <button class="button primary" type="submit" :disabled="savingDevice || !deviceDraft.sceneKey.trim() || !deviceDraft.deviceCode.trim()"><Save :size="16" />{{ savingDevice ? "保存中" : deviceDialogMode === "create" ? "确认新增" : "保存修改" }}</button>
        </div>
      </form>
    </div>

    <div v-if="settingsDialogOpen" class="modal-backdrop" @click.self="settingsDialogOpen = false">
      <form class="modal-panel settings-modal-panel" @submit.prevent="saveSettings">
        <div class="section-header">
          <div><span class="eyebrow">MQTT RUNTIME</span><h2>设备控制设置</h2></div>
          <TimerReset :size="20" />
        </div>
        <div class="form-stack">
          <label class="form-field">
            <span>等待设备操作超时</span>
            <div class="number-unit-input"><input v-model.number="commandTimeoutSeconds" type="number" min="5" max="300" step="1" /><span>秒</span></div>
            <small>命令发出后，在此时间内未收到匹配的 ACK 将标记为 TIMEOUT。支持 5 到 300 秒。</small>
          </label>
        </div>
        <p v-if="settingsError" class="form-error"><Send :size="16" />{{ settingsError }}</p>
        <div class="modal-actions">
          <button class="button secondary" type="button" :disabled="savingSettings" @click="settingsDialogOpen = false">取消</button>
          <button class="button primary" type="submit" :disabled="savingSettings"><Save :size="16" />{{ savingSettings ? "保存中" : "保存设置" }}</button>
        </div>
      </form>
    </div>
  </div>
</template>
